// Centralized labels and suggested-fix copy for COD-check audit findings.
// Used by both the legacy CodChecksPage and the new Daily Audit pages.

export const STATUS_TONES = {
  PENDING_REVIEW: 'neutral',
  AUDIT_PASSED: 'success',
  AUDIT_FLAGGED: 'danger',
  AWAITING_VENDOR_PROFILE: 'action',
  AWAITING_DOC_REVIEW: 'insight',
  IGNORED: 'neutral',
};

export const STATUS_LABELS = {
  PENDING_REVIEW: 'Pending review',
  AUDIT_PASSED: 'Audit passed',
  AUDIT_FLAGGED: 'Flagged',
  AWAITING_VENDOR_PROFILE: 'Vendor profile needed',
  AWAITING_DOC_REVIEW: 'Doc review',
  IGNORED: 'Ignored',
};

export const FINDING_LABELS = {
  no_ro_number: 'No RO# on invoice',
  handwritten_ro_needs_review: 'Handwritten RO# — verify',
  atypical_inventory_item: 'Atypical for vendor',
  unknown_vendor_no_profile: 'No vendor profile',
  vendor_profile_pending_approval: 'Vendor profile pending approval',
  vendor_was_shop_name: 'AI extracted shop name as vendor',
  vendor_name_missing: 'Vendor not extracted',
  cost_mismatch: 'Cost mismatch',
  cost_mismatch_transmission: 'Cost mismatch (transmission)',
  missing_tax_or_shipping: 'May be missing tax/shipping',
  part_not_on_ro: 'Part not on RO',
  on_deleted_ro: 'On deleted RO',
  no_receive_date: 'No receive date',
  audit_query_failed: 'Audit query error',
  ro_not_found_in_tekmetric: 'RO# not in Tekmetric',
  po_not_found_in_tekmetric: 'PO# not in Tekmetric',
  invoice_parts_not_on_ro: 'Invoice parts not on RO',
  vendor_confidence_medium: 'Vendor confidence: medium',
  check_payment_mismatch: 'Check payment mismatch',
  missing_from_cover_sheet: 'Stub not on cover sheet',
  no_backing_invoices: 'No invoice pages',
  invoice_sum_mismatch: 'Invoices ≠ check (legacy)',
  check_overpayment: 'Check overpaid (legacy)',
  invoices_exceed_check: 'Invoices exceed check (legacy)',
  po_no_parts: 'PO has no parts',
  sublet_no_ro_number: 'Sublet missing RO#',
  vendor_ignored: 'Vendor marked ignored',
  // V2 extraction safeguards (Phase A)
  scan_order_ambiguous: 'Scan order unclear — re-upload stub-first',
  orphan_invoice_no_stub: 'Invoice page appeared before any stub',
  missing_stub_for_cover_sheet: 'Cover sheet lists this check — no stub found in PDF',
  line_extraction_incomplete: 'Line totals don’t add to reported invoice total',
  credit_memo_detected: 'Credit memo detected',
  sublet_not_on_ro: 'Sublet vendor is not listed as a sublet on the matched RO',
  sublet_cost_mismatch: 'Sublet cost on invoice ≠ sublet cost on RO',
  sublet_matched_as_part: 'Sublet appears to have been entered as a part line — verify',
};

// One clear, actionable sentence per finding. Auditor reads this, knows what to do.
export const FINDING_PROBLEMS = {
  no_ro_number: 'No RO# was extracted from this invoice.',
  handwritten_ro_needs_review: 'The RO# on this invoice was handwritten and the AI is not certain.',
  atypical_inventory_item: 'A part on this invoice is not what this vendor usually sells.',
  unknown_vendor_no_profile: 'No vendor profile exists for this payee.',
  vendor_profile_pending_approval: 'The vendor profile for this payee is still pending approval.',
  vendor_was_shop_name: 'The AI mistook our shop name for the vendor name.',
  vendor_name_missing: 'The AI could not extract a vendor name.',
  cost_mismatch: 'The invoice cost does not match the cost in Tekmetric.',
  cost_mismatch_transmission: 'The transmission cost on the invoice does not match Tekmetric.',
  missing_tax_or_shipping: 'Tax or shipping may be missing from the invoice total.',
  part_not_on_ro: 'A part on this invoice is not on the matched RO in Tekmetric.',
  on_deleted_ro: 'The matched RO has been deleted in Tekmetric.',
  no_receive_date: 'The PO line has no receive date in Tekmetric.',
  audit_query_failed: 'The audit query against Tekmetric failed for this item.',
  ro_not_found_in_tekmetric: 'The RO# extracted from the invoice does not exist in Tekmetric.',
  po_not_found_in_tekmetric: 'The PO# extracted from the invoice does not exist in Tekmetric.',
  invoice_parts_not_on_ro: 'Some parts on this invoice are not on the matched RO.',
  vendor_confidence_medium: 'The AI is only moderately confident about the vendor name.',
  check_payment_mismatch:
    'The check amount does not match the sum of invoices (and the gap is not explained by a core netting).',
  missing_from_cover_sheet:
    'A check stub was found in the PDF, but the cover sheet does not list this check.',
  no_backing_invoices: 'No invoice pages were found in the PDF for this check.',
  invoice_sum_mismatch: 'Invoices total does not match the check amount (legacy).',
  check_overpayment: 'Check paid more than the invoices total (legacy).',
  invoices_exceed_check: 'Invoices exceed the check amount (legacy).',
  po_no_parts: 'The matched PO has no parts.',
  sublet_no_ro_number: 'This sublet invoice is missing an RO#.',
  vendor_ignored: 'This vendor has been marked as ignored — no audit applied.',
  scan_order_ambiguous: 'The PDF pages look like they were uploaded invoice-first or in mixed order. Grouping may be wrong.',
  orphan_invoice_no_stub: 'This invoice appeared before any check stub in the PDF, so we had to guess its parent check.',
  missing_stub_for_cover_sheet: 'The cover sheet lists this check, but no physical check stub was found in the PDF.',
  line_extraction_incomplete: 'The sum of invoice line totals doesn’t match the invoice’s reported total (tax/shipping/discount may have been missed).',
  credit_memo_detected: 'This page is a credit memo (negative/credit invoice). Full detailed parsing deferred to a later build.',
};

// V2 extraction fixes (Phase A)
export const FINDING_SUGGESTED_FIX_V2 = {
  scan_order_ambiguous:
    'Ask the store to re-upload the daily PDF in stub → invoice order (check stub page first, then the invoices that back it).',
  orphan_invoice_no_stub:
    'Verify the parent check manually. If the orphan invoice matches a cover-sheet check by vendor+amount, reassign with the pencil edit.',
  missing_stub_for_cover_sheet:
    'The check was written per the cover sheet but the physical stub wasn’t scanned. Ask the store to upload the missing stub page.',
  line_extraction_incomplete:
    'Open the invoice preview and edit the line items. Check for missed tax, shipping, or discount lines. Save & re-audit.',
  credit_memo_detected:
    'No action needed — the credit memo is tracked as a pending COD credit for this vendor. Apply it via the Edit modal on a check that used it.',
  sublet_not_on_ro:
    'This is a sublet vendor (e.g. alignment shop, tow truck, machine shop) but the RO in Tekmetric has no matching sublet line for this vendor. Add the sublet to the RO in Tekmetric, or reassign to a different RO.',
  sublet_cost_mismatch:
    'The sublet on the RO has a different cost than this invoice. Update the sublet cost in Tekmetric or confirm with the vendor why the amount differs.',
  sublet_matched_as_part:
    'No sublet line found on the RO, but a part line with matching cost was — looks like the service was entered as a part. Verify with the RO in Tekmetric; if correct, re-enter as a sublet line for cleaner accounting.',
};

// Suggested fix per finding — what the auditor should DO.
export const FINDING_SUGGESTED_FIX = {
  no_ro_number:
    'Open the invoice preview, look for a handwritten RO# in the margins, then enter it via the pencil edit.',
  handwritten_ro_needs_review:
    'Open the preview and verify the handwritten RO#. If correct, click "Mark passed". If wrong, edit it.',
  atypical_inventory_item:
    'Verify the part really came from this vendor. If yes, mark as a one-off okay. If a kit, toggle "Bundle / Kit purchase".',
  unknown_vendor_no_profile:
    'Create a vendor profile for this payee from "Vendors Needed", or reassign to an existing profile via the pencil edit.',
  vendor_profile_pending_approval:
    'Approve the vendor profile in "Vendor Profiles", then this audit will re-run.',
  vendor_was_shop_name:
    'Reassign the correct vendor via the pencil edit. The system will learn this alias.',
  vendor_name_missing:
    'Open the preview to read the vendor name, then assign via the pencil edit.',
  cost_mismatch:
    'Compare invoice line totals with Tekmetric RO part costs. Edit the cost or mark as bundle if the totals match overall.',
  cost_mismatch_transmission:
    'Verify the transmission cost. If correct, mark as okay. Otherwise edit the cost or follow up with the vendor.',
  missing_tax_or_shipping:
    'Confirm the invoice includes tax + shipping. Adjust the amount via the pencil edit if needed.',
  part_not_on_ro:
    'Reassign the part to the right RO in Tekmetric, or mark this invoice as a Bundle / Kit purchase.',
  on_deleted_ro:
    'The RO was deleted. Find the correct RO and edit the RO# via the pencil edit.',
  no_receive_date:
    'Mark the part as received in Tekmetric, then re-run the audit.',
  audit_query_failed:
    'Try again, or check the API logs. Often this is a Tekmetric connection blip.',
  ro_not_found_in_tekmetric:
    'Verify the RO# on the invoice and edit it via the pencil. The AI may have mis-read a digit.',
  po_not_found_in_tekmetric:
    'Verify the PO# on the invoice. The AI may have mis-read a digit.',
  invoice_parts_not_on_ro:
    'Reassign parts to the right RO, or toggle "Bundle / Kit purchase" if multiple components add up to one Tekmetric kit.',
  vendor_confidence_medium:
    'Confirm the vendor in the preview. If wrong, reassign via the pencil edit.',
  check_payment_mismatch:
    'Compare invoice totals with the check amount. The gap could be a discount, a missing invoice, or a wrong-grouping.',
  missing_from_cover_sheet:
    'Confirm this check really exists. If the cover sheet missed it, leave as flagged so it is on the audit log. Otherwise mark ignored.',
  no_backing_invoices:
    'Check whether the invoices were uploaded. If missing, ask the store to scan them.',
  po_no_parts:
    'Add the parts to the PO in Tekmetric, then re-run the audit.',
  sublet_no_ro_number:
    'Get the sublet RO# from the technician and enter it via the pencil edit.',
  vendor_ignored:
    'No action needed — this vendor is intentionally ignored.',
};

// Merge V2 fixes into the main map for convenience. MUST come after both
// `FINDING_SUGGESTED_FIX` and `FINDING_SUGGESTED_FIX_V2` are declared —
// referencing either one before its `const` is evaluated is a TDZ error
// that crashes the minified production bundle.
Object.assign(FINDING_SUGGESTED_FIX, FINDING_SUGGESTED_FIX_V2);
